import React, { useEffect, useMemo, useState } from 'react';
import { Btn, DataTable, Loading, Msg, TextInput, fmtDate, panelGet, useAdminGet } from '../kit';
import { DateRange, Drawer } from '../email/emailKit';
import { AdminLink } from '../adminNav';
import { asArray, asStr, first, fmt, num, rowsOf, secs } from './shared';

/**
 * Admin → Onboarding → People: one row per person who saw the onboarding
 * modal — what they chose, how long each step took, which 3 picks they got,
 * what they did on Cosmos after (picks opened, locks hit) and whether a trial
 * or a paid plan followed. A row opens the person's journey: their
 * onboarding_events, the related site clicks and their billing rows, merged
 * in time order (onboarding/person).
 */

const PAGE = 50;

const OUTCOMES: Record<string, string> = {
  '': 'Any outcome', industry_keyword: 'Industry + keyword', industry_only: 'Industry only',
  skipped: 'Skipped step 1', unfinished: 'Not finished',
};

const OUTCOME_TEXT: Record<string, string> = {
  industry_keyword: 'Industry + keyword', industry_only: 'Industry only', skipped: 'Skipped',
};

const titlesOf = (v: any): string => {
  if (!v) return '';
  if (Array.isArray(v)) return v.map(x => (typeof x === 'string' ? x : asStr(first(x?.title, x?.id)))).filter(Boolean).join(' · ');
  return asStr(v);
};

const personRow = (r: any): Record<string, any> => ({
  email: asStr(first(r.email, r.user_email)),
  created_at: first(r.signed_up, r.created_at, r.user_created_at) ?? null,
  shown_at: first(r.shown_at, r.started_at, r.first_shown) ?? null,
  outcome: OUTCOME_TEXT[asStr(r.outcome)] || asStr(r.outcome) || (r.finished_at ? 'Finished' : 'Not finished'),
  industry: asStr(r.industry),
  keyword: asStr(r.keyword),
  step1: secs(first(r.s_step1, r.ms_step1 !== undefined && r.ms_step1 !== null ? num(r.ms_step1)! / 1000 : undefined)),
  step2: secs(first(r.s_step2, r.ms_step2 !== undefined && r.ms_step2 !== null ? num(r.ms_step2)! / 1000 : undefined)),
  picks: titlesOf(first(r.picks_titles, r.picks)),
  opened: num(first(r.picks_opened, r.opened)),
  locks: num(first(r.locks_hit, r.locks)),
  trial_at: first(r.trial_at) ?? null,
  trial_origin: asStr(r.trial_origin),
  paid_at: first(r.paid_at) ?? null,
  _user_id: asStr(first(r.user_id, r.id)),
  _pre: r.pre_tracking === true,
});

const Person: React.FC<{ userId: string; email: string; onClose: () => void }> = ({ userId, email, onClose }) => {
  const [data, setData] = useState<any>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let alive = true;
    panelGet('onboarding', 'person', { user_id: userId }).then(({ data: d, error }) => {
      if (!alive) return;
      if (error) setErr(error); else setData(d);
    });
    return () => { alive = false; };
  }, [userId]);

  const timeline = useMemo(() => {
    if (!data) return [];
    const direct = rowsOf(data, 'timeline');
    if (direct.length) {
      return direct.map((r: any) => (r && typeof r === 'object' && 'at' in r && !('when' in r)
        ? { when: r.at, ...Object.fromEntries(Object.entries(r).filter(([k]) => k !== 'at')) } : r));
    }
    const out: Record<string, any>[] = [];
    rowsOf(data, 'events', 'onboarding_events').forEach((e: any) => out.push({
      when: e.at, where: 'onboarding', what: asStr(e.event),
      detail: [e.step ? `step ${e.step}` : '', e.industry, e.keyword, e.ms_on_step ? secs(num(e.ms_on_step)! / 1000) : '']
        .filter(Boolean).join(' · '),
    }));
    asArray(data.site_events).forEach((e: any) => out.push({
      when: e.at, where: 'site', what: asStr(e.action),
      detail: e.props && typeof e.props === 'object'
        ? Object.entries(e.props).filter(([k]) => !['ob_id', 'flow', 'entry', 'ms_since_open'].includes(k))
          .slice(0, 6).map(([k, v]) => `${k}=${typeof v === 'object' ? JSON.stringify(v) : v}`).join(' ')
        : '',
    }));
    asArray(data.billing).forEach((b: any) => out.push({
      when: b.created_at, where: 'billing',
      what: b.is_trial ? 'trial' : asStr(first(b.plan_code, 'subscription')),
      detail: [b.status, b.source, b.origin || b.trial_form?.origin].filter(Boolean).join(' · '),
    }));
    return out.sort((a, b) => String(a.when || '').localeCompare(String(b.when || '')));
  }, [data]);

  const progress = data ? first(data.progress, data.row) : null;
  return (
    <Drawer title={email || userId} subtitle="Onboarding journey" onClose={onClose}>
      <div className="flex gap-3 flex-wrap text-xs">
        <AdminLink to={{ group: 'user360', email: email || userId }}>Open in User 360</AdminLink>
        <AdminLink to={{ group: 'funnel' }}>Checkout funnel</AdminLink>
      </div>
      {err && <Msg kind="error">{err}</Msg>}
      {!data && !err && <Loading />}
      {progress && typeof progress === 'object' && (
        <div className="bg-white rounded-xl border border-grayscale-300 p-3 grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
          {Object.entries(progress).filter(([k]) => !['user_id'].includes(k)).map(([k, v]) => (
            <div key={k} className="flex justify-between gap-3 border-b border-grayscale-200 py-0.5">
              <span className="text-grayscale-1100 text-xs">{k.replace(/_/g, ' ')}</span>
              <span className="text-grayscale-1300 text-xs text-right break-all">
                {v === null || v === undefined || v === '' ? '—'
                  : /_at$/.test(k) ? fmtDate(v)
                  : typeof v === 'object' ? JSON.stringify(v) : String(v)}
              </span>
            </div>
          ))}
        </div>
      )}
      {data && (
        <DataTable rows={timeline} empty="No events recorded."
          csvName={`onboarding_journey_${userId}`} maxHeight="60vh" />
      )}
    </Drawer>
  );
};

const People: React.FC<{ industries?: string[] }> = ({ industries: known }) => {
  const [q, setQ] = useState('');
  const [qApplied, setQApplied] = useState('');
  const [outcome, setOutcome] = useState('');
  const [industry, setIndustry] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(0);
  const [open, setOpen] = useState<{ id: string; email: string } | null>(null);

  useEffect(() => {
    const t = setTimeout(() => { setQApplied(q.trim()); setPage(0); }, 350);
    return () => clearTimeout(t);
  }, [q]);

  const params = useMemo(() => {
    const p: Record<string, string | number> = { page, page_size: PAGE };
    if (qApplied) p.q = qApplied;
    if (outcome) p.outcome = outcome;
    if (industry) p.industry = industry;
    if (from) p.from = from;
    if (to) p.to = to;
    return p;
  }, [page, qApplied, outcome, industry, from, to]);
  const res = useAdminGet<any>('onboarding', 'people', params, [JSON.stringify(params)]);
  const rows = rowsOf(res.data, 'people').map(personRow);
  const total = num(first(res.data?.total, res.data?.count));
  const industries: string[] = (known && known.length ? known : asArray(res.data?.industries).map(asStr))
    .filter(Boolean);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
          <span>Search</span>
          <TextInput value={q} onChange={setQ} placeholder="E-mail or name" />
        </label>
        <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
          <span>Outcome</span>
          <select value={outcome} onChange={e => { setOutcome(e.target.value); setPage(0); }}
            className="h-9 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm">
            {Object.entries(OUTCOMES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
          <span>Industry</span>
          <select value={industry} onChange={e => { setIndustry(e.target.value); setPage(0); }}
            className="h-9 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm max-w-[16rem]">
            <option value="">Any industry</option>
            {(industries.length ? industries : (industry ? [industry] : [])).map(i => <option key={i} value={i}>{i}</option>)}
          </select>
        </label>
        <DateRange label="Shown (India days)" from={from} to={to} onChange={(f, t) => { setFrom(f); setTo(t); setPage(0); }} />
        <Btn small onClick={res.reload}>Refresh</Btn>
      </div>
      {res.error && <Msg kind="error">{res.error}</Msg>}
      {res.loading && !res.data ? <Loading /> : (
        <DataTable rows={rows}
          columns={['email', 'created_at', 'shown_at', 'outcome', 'industry', 'keyword', 'step1', 'step2', 'picks',
            'opened', 'locks', 'trial_at', 'trial_origin', 'paid_at']}
          labels={{ created_at: 'Signed up', shown_at: 'Shown', step1: 'Step 1', step2: 'Step 2', picks: 'Picks served', opened: 'Picks opened',
            locks: 'Locks hit', trial_at: 'Trial', trial_origin: 'Trial origin', paid_at: 'Paid' }}
          render={{
            email: r => (
              <button type="button" className="text-primary-700 underline underline-offset-2 text-left"
                onClick={() => setOpen({ id: r._user_id, email: r.email })}>
                {r.email || r._user_id}{r._pre ? <span className="text-grayscale-1100 no-underline"> (pre-tracking)</span> : null}
              </button>
            ),
            picks: r => <span className="block max-w-[22rem] truncate" title={r.picks}>{r.picks || '—'}</span>,
          }}
          empty="Nobody matches." csvName="onboarding_people" maxHeight="36rem" />
      )}
      <div className="flex items-center gap-2 text-xs text-grayscale-1100">
        <Btn small onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>← Previous</Btn>
        <span>Page {page + 1}{total !== null ? ` of ${Math.max(1, Math.ceil(total / PAGE))} · ${fmt(total)} people` : ''}</span>
        <Btn small onClick={() => setPage(p => p + 1)}
          disabled={total !== null ? (page + 1) * PAGE >= total : rows.length < PAGE}>Next →</Btn>
      </div>
      {open && <Person userId={open.id} email={open.email} onClose={() => setOpen(null)} />}
    </div>
  );
};

export default People;
